/**
 * The client agreement built from a quote (owner 2026-10-16: "vi behöver dem också helhet om vi ska kunna skicka för
 * signering"; "Båda": special terms per agreement and templates an admin can edit). The texts are versioned templates
 * (version 1 = KFF's templates in docs/avtalsmallar-2026-10-02.md): the frame agreement with subscription (Abonnemang
 * v4, 1.1) or per production (Projekt v4, 2.1), KFF's general terms (Allmänna villkor 2026:2) and the data processing
 * agreement (PUB) as appendices. Version 1 is a draft waiting for legal review.
 *
 * Template syntax (DATA-1026): `{{name}}` is filled from the quote; a paragraph whose value is missing is left out, and
 * `[[ … ]]` is optional text that is left out when a value inside it is missing. A paragraph that is just a list value
 * (`{{calloff_rows}}`) becomes one paragraph per row. `{{ref:key}}` is the number of the section with that key. A section
 * with `when` is only included when that value is there.
 */
import { agreementPackages, contentsSv, findPackage, isFrameOnly, kr, minProductionsOf, tierFor, QUOTE_TERMS, type ContractParty, type PackageDef, type PriceListData, type QuoteSummary, type QuoteTerms } from './pricing.js';
import { contractTravelTerm } from './travel.js';

/** KFF's legal entity in the agreements (docs/avtalsmallar-2026-10-02.md § 0). */
export const KFF_PARTY = { name: 'Färdiga Film Sweden AB', orgNo: '559265-8834' } as const;

export interface TemplateSection {
  key: string;
  heading: string;
  paras: string[];
  /** Only included when this value is there (options, start-up fee, special terms) */
  when?: string;
  /** Only included when this value is missing (e.g. no fee) */
  unless?: string;
}

export interface TemplateDoc {
  title: string;
  intro: string[];
  sections: TemplateSection[];
}

export interface ContractTemplatesData {
  subscription: TemplateDoc;
  project: TemplateDoc;
  /** Allmänna villkor, with the version label the agreements refer to ("2026:2") and the date it applies from */
  terms: TemplateDoc & { version: string; effectiveFrom: string };
  dpa: TemplateDoc;
  /** Modellkontrakt for actors on a project (owner 2026-10-16). Versions saved before it existed get the seed text. */
  model: TemplateDoc;
}

export interface ContractTemplates extends ContractTemplatesData {
  version: number;
  note: string;
  createdAt: string;
  createdBy: string | null;
}

export type TemplateKey = keyof ContractTemplatesData;
export const TEMPLATE_KEYS: TemplateKey[] = ['subscription', 'project', 'terms', 'dpa', 'model'];

/** The values each template can use, for the editor's help text. */
export const TEMPLATE_FIELDS: Record<TemplateKey, string[]> = {
  subscription: ['kff', 'kff_orgno', 'client', 'orgno', 'address', 'ref', 'package', 'package_key', 'price', 'calloff_rows', 'included', 'hourly_rate', 'option_rows', 'startup_fee', 'ads', 'payment_days', 'start', 'period', 'min_term', 'notice_months', 'special_rows', 'client_contact', 'kff_contact', 'terms_version', 'ref:<key>'],
  project: ['kff', 'kff_orgno', 'client', 'orgno', 'address', 'ref', 'project', 'package', 'package_key', 'min_productions', 'period_months', 'min_value', 'tier', 'calloff_rows', 'production_def', 'included', 'hourly_rate', 'option_rows', 'startup_fee', 'ads', 'payment_days', 'start', 'timeline', 'cancel_early', 'cancel_late', 'cancel_days', 'cancel_ref', 'reschedule_note', 'special_rows', 'client_contact', 'kff_contact', 'terms_version', 'ref:<key>'],
  terms: ['kff', 'kff_orgno', 'terms_version', 'terms_date'],
  dpa: ['kff', 'kff_orgno', 'client', 'orgno', 'ref'],
  model: ['kff', 'kff_orgno', 'actor', 'birth_date', 'actor_contact', 'guardian', 'project', 'project_code', 'client', 'role', 'shoot_date', 'fee', 'usage', 'producer', 'invoice_ref', 'billing']
};

export interface ContractSection {
  heading: string;
  paras: string[];
}

export interface ContractPart {
  key: 'main' | 'terms' | 'dpa';
  title: string;
  intro: string[];
  sections: ContractSection[];
  /** Signature block at the end of this part */
  signed: boolean;
}

export interface ContractDoc {
  parts: ContractPart[];
  signers: { party: string; name: string | null; title: string | null }[];
  /** What has to be filled in before the agreement can be signed */
  missing: string[];
  /** Placeholders in the templates that the Hub doesn't know (typos in an edited template) */
  unknown: string[];
  templateVersion: number;
  termsVersion: string;
}

export interface ContractInput {
  ref: string;
  clientName: string;
  title: string;
  contactName: string | null;
  contactEmail: string | null;
  kffContact: string | null;
  terms: QuoteTerms;
  summary: QuoteSummary;
}

type Vals = Record<string, string | string[] | null | undefined>;

const VAR_RE = /\{\{([a-z_]+(?::[a-z0-9_-]+)?)\}\}/g;

/** Fill one paragraph; null = leave the paragraph out. */
function fill(text: string, get: (name: string) => string | string[] | null | undefined, unknown: Set<string>): string | null {
  let missing = false;
  const sub = (t: string) => {
    let gone = false;
    const out = t.replace(VAR_RE, (whole, name: string) => {
      const v = get(name);
      if (v === undefined) {
        unknown.add(name);
        return whole;
      }
      if (v === null || (Array.isArray(v) && !v.length)) {
        gone = true;
        return '';
      }
      return Array.isArray(v) ? v.join(', ') : v;
    });
    return gone ? null : out;
  };
  const withOptional = text.replace(/\[\[([\s\S]*?)\]\]/g, (_, inner: string) => sub(inner) ?? '');
  const out = sub(withOptional);
  if (out === null) missing = true;
  return missing ? null : out;
}

/** Render a template: sections in order, numbered (main agreement) and with references resolved. */
export function renderTemplate(doc: TemplateDoc, vals: Vals, opts: { numbered: boolean }, unknown: Set<string> = new Set()): { title: string; intro: string[]; sections: ContractSection[] } {
  const present = (v: string | string[] | null | undefined) => v !== null && v !== undefined && !(Array.isArray(v) && !v.length) && v !== '';
  const kept = doc.sections.filter((s) => (!s.when || present(vals[s.when])) && (!s.unless || !present(vals[s.unless])));
  const numbers = new Map<string, number>();
  let n = 0;
  for (const s of kept) if (opts.numbered && s.key !== 'signatures') numbers.set(s.key, ++n);
  const get = (name: string) => {
    if (name.startsWith('ref:')) {
      const k = name.slice(4);
      if (numbers.has(k)) return String(numbers.get(k));
      return doc.sections.some((s) => s.key === k) ? null : undefined;
    }
    return name in vals ? vals[name] : undefined;
  };
  const paras = (list: string[]) =>
    list.flatMap((p) => {
      const whole = /^\s*\{\{([a-z_]+)\}\}\s*$/.exec(p);
      const v = whole ? get(whole[1]!) : undefined;
      if (whole && Array.isArray(v)) return v;
      const out = fill(p, get, unknown);
      return out === null || !out.trim() ? [] : [out.trim()];
    });
  return {
    title: fill(doc.title, get, unknown) ?? doc.title,
    intro: paras(doc.intro),
    sections: kept
      .map((s) => ({ heading: numbers.has(s.key) ? `${numbers.get(s.key)}. ${s.heading}` : s.heading, paras: paras(s.paras) }))
      .filter((s, i) => s.paras.length > 0 || kept[i]!.paras.length === 0)
  };
}

/** Problems with an edited set of templates (empty = can be saved). */
export function validateTemplates(t: ContractTemplatesData): string[] {
  const out: string[] = [];
  const known = (k: TemplateKey) => new Set(TEMPLATE_FIELDS[k].filter((x) => !x.startsWith('ref:')));
  for (const k of TEMPLATE_KEYS) {
    const d = t[k];
    if (!d || typeof d.title !== 'string' || !Array.isArray(d.sections) || !Array.isArray(d.intro)) {
      out.push(`${k}: the template is missing`);
      continue;
    }
    if (!d.title.trim()) out.push(`${k}: give the template a title`);
    if (!d.sections.length) out.push(`${k}: add at least one section`);
    const keys = new Set<string>();
    for (const s of d.sections) {
      if (!s || typeof s.heading !== 'string' || !Array.isArray(s.paras) || s.paras.some((p) => typeof p !== 'string')) {
        out.push(`${k}: a section is broken`);
        continue;
      }
      if (!s.heading.trim()) out.push(`${k}: every section needs a heading`);
      if (!s.key || keys.has(s.key)) out.push(`${k}: section keys must be unique (${s.key || 'empty'})`);
      keys.add(s.key);
    }
    for (const text of [d.title, ...d.intro, ...d.sections.flatMap((s) => s.paras ?? [])]) {
      for (const m of String(text).matchAll(VAR_RE)) {
        const name = m[1]!;
        if (name.startsWith('ref:') ? !d.sections.some((s) => s.key === name.slice(4)) : !known(k).has(name)) out.push(`${k}: {{${name}}} is not a field the Hub knows`);
      }
    }
  }
  if (t.terms && !/^\S{1,20}$/.test(t.terms.version ?? '')) out.push('terms: give Allmänna villkor a version label, e.g. 2026:3');
  if (t.terms && !/^\d{4}-\d{2}-\d{2}$/.test(t.terms.effectiveFrom ?? '')) out.push('terms: give the date Allmänna villkor apply from');
  return [...new Set(out)];
}

/** The templates version that applies to an agreement: the one in force when it was sent, else the latest. */
export function templatesFor(all: ContractTemplates[], sentAt: string | null): ContractTemplates {
  if (sentAt) {
    const at = all.filter((t) => t.createdAt <= sentAt).pop();
    if (at) return at;
  }
  return all.at(-1)!;
}

const n = (x: number) => kr(x).replace(' kr', ' kronor');
const plural = (k: number, one: string, many: string) => `${k} ${k === 1 ? one : many}`;
const noShoot = (p: PackageDef) => p.shootH === 0 || /ingen inspelning/i.test(p.project?.videos ?? '');
/** The package's contents on one line, without the "always included" line. */
const contentsLine = (pl: PriceListData, p: PackageDef, t: QuoteTerms) =>
  contentsSv(pl, p, t.kind, t.customContents)
    .slice(0, p.custom ? 1 : -1)
    .map((x, i) => (i === 0 ? x : x[0]!.toLowerCase() + x.slice(1)))
    .join(', ');

export function buildContract(pl: PriceListData, a: ContractInput, tpl: ContractTemplates): ContractDoc {
  const t = a.terms;
  const s = a.summary;
  const party: ContractParty = t.party ?? { orgNo: null, address: null, signerName: null, signerTitle: null };
  const pkg = findPackage(pl, t.packageKey);
  const price = t.price ?? s.list.price ?? 0;
  const hourly = s.options.find((o) => o.key === 'hour')?.price ?? pl.addOns.find((o) => o.key === 'hour')?.price ?? 1500;
  const startup = s.lines.find((l) => l.key === 'startup')?.amount ?? 0;
  const ads = s.lines.find((l) => l.key === 'ads');
  // a travel rule other than the standard is a special term too (owner 2026-10-29)
  const travel = contractTravelTerm(t.travel, hourly);
  const special = [...(travel ? [travel] : []), ...(t.specialTerms ?? '').split(/\n+/).map((x) => x.trim()).filter(Boolean)];
  const orgNo = party.orgNo ?? '[org.nr]';
  const common: Vals = {
    kff: KFF_PARTY.name,
    kff_orgno: KFF_PARTY.orgNo,
    client: a.clientName,
    orgno: orgNo,
    address: party.address ?? '[adress]',
    ref: a.ref,
    terms_version: tpl.terms.version,
    terms_date: tpl.terms.effectiveFrom
  };
  const vals: Vals = {
    ...common,
    project: a.title || null,
    package: pkg ? `${pkg.key} (${pkg.sv})` : t.packageKey,
    package_key: pkg?.key ?? t.packageKey,
    price: n(price),
    included: pl.included.sv,
    hourly_rate: n(hourly),
    option_rows: s.options.map((o) => `– ${o.sv}: ${o.from ? 'från ' : ''}${n(o.price)}${o.unit === 'h' ? ' per timme' : ''}`),
    startup_fee: startup > 0 ? n(startup) : null,
    ads: ads ? `Annonshantering ingår med ${n(ads.amount)} per månad exklusive moms (${n(pl.ads.fixed)} plus ${pl.ads.pct} % av en mediebudget om ${n(t.adsMediaBudget ?? 0)}). Mediebudgeten betalas direkt av Kunden.` : null,
    payment_days: String(QUOTE_TERMS.paymentDays),
    start: t.startDate ? `den ${t.startDate}` : 'den dag avtalet har undertecknats av båda parter',
    special_rows: special,
    client_contact: [a.contactName, a.contactEmail].filter(Boolean).join(', ') || '[namn]',
    kff_contact: a.kffContact ?? '[namn]',
    cancel_early: `${QUOTE_TERMS.cancelEarlyPct} %`,
    cancel_late: `${QUOTE_TERMS.cancelLatePct} %`,
    cancel_days: String(QUOTE_TERMS.cancelDays)
  };
  if (t.kind === 'subscription') {
    vals.calloff_rows = agreementPackages(pl, { ...t, price })
      .map((c) => {
        const p = findPackage(pl, c.key);
        return p ? `– ${c.key} (${c.sv}): ${n(c.price)} per månad – ${contentsLine(pl, p, t)}` : null;
      })
      .filter((x): x is string => x !== null);
    vals.min_term = t.periodMonths ? plural(t.periodMonths, 'månad', 'månader') : null;
    vals.period = t.periodMonths ? `Avtalet har en minsta avtalstid om ${plural(t.periodMonths, 'månad', 'månader')} från startdatum. Därefter löper det tills vidare.` : 'Avtalet löper tills vidare.';
    vals.notice_months = String(QUOTE_TERMS.noticeMonths);
  } else {
    const frame = isFrameOnly(t);
    const min = Math.max(1, minProductionsOf(t));
    const tier = tierFor(pl, min);
    const defs = s.callOff.map((c) => findPackage(pl, c.key)).filter((p): p is PackageDef => p !== null);
    const shoot = defs.filter((p) => !noShoot(p)).length;
    const without = defs.length - shoot;
    vals.min_productions = frame ? null : plural(min, 'produktion', 'produktioner');
    vals.period_months = plural(t.periodMonths ?? 2, 'månad', 'månader');
    vals.min_value = frame ? null : min === 1 ? n(price) : `${min} × ${n(price)} = ${n(min * price)}`;
    vals.tier = tier && tier.pct > 0 ? `${tier.pct} % rabatt mot listpris` : 'listpris';
    vals.calloff_rows = s.callOff.map((c) => {
      const p = findPackage(pl, c.key);
      return `– ${c.key} (${c.sv}): ${n(c.price)} per produktion${p ? ` – ${contentsLine(pl, p, t)}` : ''}`;
    });
    vals.production_def =
      without === 0
        ? 'En produktion är hela vägen från idé och inspelning till redigering och leverans.'
        : shoot === 0
          ? 'En produktion är hela vägen från idé till redigering och leverans, utan inspelning.'
          : `En produktion är hela vägen från idé och inspelning till redigering och leverans; för paket utan inspelning (${defs.filter(noShoot).map((p) => p.key).join(' och ')}) från idé till redigering och leverans, utan inspelning.`;
    vals.timeline = [
      shoot > 0 ? `${without > 0 ? 'För paket med inspelning sker inspelningen' : 'Inspelning sker'} på ett datum som parterna kommer överens om, och första version levereras inom fem (5) arbetsdagar efter inspelningen.` : null,
      without > 0 ? `${shoot > 0 ? 'För paket utan inspelning levereras första version' : 'Första version levereras'} enligt den gemensamma projektplanen.` : null
    ]
      .filter(Boolean)
      .join(' ');
    vals.cancel_ref = shoot > 0 ? (without > 0 ? 'inspelningen eller, utan inspelning, planerad start' : 'inspelningen') : 'planerad start';
    vals.reschedule_note = shoot > 0 ? 'Ombokning av inspelning följer Allmänna villkor § 13.' : null;
  }

  const unknown = new Set<string>();
  const main = renderTemplate(isFrameOnly(t) ? frameOnly(tpl.project) : tpl[t.kind], vals, { numbered: true }, unknown);
  const terms = renderTemplate(tpl.terms, common, { numbered: false }, unknown);
  const dpa = renderTemplate(tpl.dpa, common, { numbered: false }, unknown);
  const missing: string[] = [];
  if (!party.orgNo) missing.push('Kundens org.nr');
  if (!party.address) missing.push('Kundens adress');
  if (!party.signerName) missing.push('Vem som undertecknar för kunden');
  if (!a.kffContact) missing.push('Vem som undertecknar för KFF (ansvarig)');
  return {
    parts: [
      { key: 'main', ...main, signed: true },
      { key: 'terms', ...terms, title: `Bilaga 1 – ${terms.title}`, signed: false },
      { key: 'dpa', ...dpa, title: `Bilaga 2 – ${dpa.title}`, signed: false }
    ],
    signers: [
      { party: a.clientName, name: party.signerName, title: party.signerTitle },
      { party: KFF_PARTY.name, name: a.kffContact, title: null }
    ],
    missing: [...missing, ...s.missing.map((m) => `Offerten: ${m}`)],
    unknown: [...unknown],
    templateVersion: tpl.version,
    termsVersion: tpl.terms.version
  };
}

/**
 * The per-production template for a frame agreement with no minimum (owner 2026-10-29). Works on any saved template
 * version: the minimum and shortfall sections go, as does every sentence about the minimum or pointing at them, and the
 * form section says there is no minimum.
 */
const FRAME_SENTENCE = 'Avtalet har inget minimiåtagande: Kunden avropar de produktioner Kunden behöver, när Kunden behöver dem, till priserna i punkt {{ref:calloff}}.';
export function frameOnly(doc: TemplateDoc): TemplateDoc {
  const gone = new Set(['minimum', 'shortfall']);
  const aboutMinimum = (x: string) => /minimi/i.test(x) || [...gone].some((k) => x.includes(`{{ref:${k}}}`));
  const strip = (p: string) => {
    if (/^\s*\{\{[a-z_]+\}\}\s*$/.test(p)) return p;
    // sentences end at ". " outside [[ ]]; an optional part that is about the minimum goes with it
    const kept = p
      .replace(/\[\[([\s\S]*?)\]\]/g, (whole, inner: string) => (aboutMinimum(inner) ? '' : whole))
      .split(/(?<=\.)\s+(?![^[]*\]\])/)
      .filter((x) => !aboutMinimum(x));
    return kept.join(' ');
  };
  return {
    ...doc,
    sections: doc.sections
      .filter((x) => !gone.has(x.key))
      .map((x) => {
        const paras = x.paras.map(strip).filter((p) => p.trim());
        return x.key === 'form' ? { ...x, paras: [...paras, FRAME_SENTENCE] } : { ...x, paras };
      })
  };
}

const sec = (key: string, heading: string, paras: string[], when?: string): TemplateSection => ({ key, heading, paras, ...(when ? { when } : {}) });
const tsec = (heading: string, ...paras: string[]): TemplateSection => ({ key: `s${heading.split('.')[0]}`, heading, paras });

const PARTIES = 'Parter: {{kff}}, org.nr {{kff_orgno}} (”KFF”), och {{client}}, org.nr {{orgno}}, {{address}} (”Kunden”).';
const SPECIAL = sec('special', 'Särskilda villkor', ['{{special_rows}}', 'De särskilda villkoren gäller före övriga punkter i avtalet och före Allmänna villkor.'], 'special_rows');
const CONTACTS = sec('contacts', 'Kontaktpersoner', ['Kundens kontaktperson: {{client_contact}}. KFF:s ansvariga: {{kff_contact}}.']);
const APPENDICES = sec('appendices', 'Allmänna villkor och bilagor', [
  'KFF:s Allmänna villkor version {{terms_version}} (bilaga 1) gäller för avtalet och bifogas. När KFF behandlar personuppgifter för Kundens räkning gäller också Personuppgiftsbiträdesavtalet (bilaga 2). Om handlingarna säger olika saker gäller ordningen i Allmänna villkor § 19.'
]);
const SIGNATURES = sec('signatures', 'Underskrifter', ['Avtalet undertecknas av båda parter. Varje part får en signerad kopia.']);
const OPTIONS = sec('options', 'Tillval', ['Följande tillval kan avropas vid behov under avtalstiden och faktureras i efterskott när de används, till dessa priser exklusive moms:', '{{option_rows}}'], 'option_rows');
const EXTRA = 'Varje draft är en feedbackrunda efter första versionen. Extra rundor, inspelningstid, klipptid eller administration utöver paketen debiteras med {{hourly_rate}} per timme exklusive moms[[, eller enligt tillvalen i punkt {{ref:options}}]].';

/** What a model release is filled with (one actor on one project). */
export interface ReleaseInput {
  actor: string;
  birthDate: string | null;
  email: string | null;
  phone: string | null;
  guardian: string | null;
  project: string;
  projectCode: string;
  client: string;
  role: string | null;
  shootDate: string | null;
  /** kronor, excluding VAT */
  fee: number | null;
  usage: string | null;
  producer: string | null;
  /** the reference to put on the invoice (0058) */
  invoiceRef?: string | null;
  /** the company the actor invoices through: name and org.nr (0058) */
  billing?: string | null;
}

export interface ReleaseDoc {
  title: string;
  intro: string[];
  sections: ContractSection[];
  /** who signs: the actor, or the guardian for an actor under 18 */
  signer: { name: string; role: 'actor' | 'guardian' };
  unknown: string[];
  templateVersion: number;
}

/** The model release for one actor, from the template version in force. Numbered sections, references resolved. */
export function buildRelease(tpl: Pick<ContractTemplates, 'model' | 'version'>, a: ReleaseInput): ReleaseDoc {
  const unknown = new Set<string>();
  const vals: Vals = {
    kff: KFF_PARTY.name,
    kff_orgno: KFF_PARTY.orgNo,
    actor: a.actor,
    birth_date: a.birthDate,
    actor_contact: [a.email, a.phone].filter(Boolean).join(', ') || null,
    guardian: a.guardian,
    project: a.project,
    project_code: a.projectCode,
    client: a.client,
    role: a.role,
    shoot_date: a.shootDate,
    fee: a.fee !== null && a.fee > 0 ? n(a.fee) : null,
    usage: a.usage,
    producer: a.producer,
    invoice_ref: a.invoiceRef ?? null,
    billing: a.billing ?? null
  };
  const doc = renderTemplate(tpl.model, vals, { numbered: true }, unknown);
  // a paid release says how to invoice, also when the saved template does not (owner 2026-10-29: actors are paid on invoice)
  const feeText = typeof vals.fee === 'string' ? vals.fee : null;
  if (feeText && a.invoiceRef && !JSON.stringify(doc).includes(a.invoiceRef)) {
    const line = `Faktura märks med referens ${a.invoiceRef}${a.billing ? ` och ställs ut av ${a.billing}` : ''}.`;
    const fee = doc.sections.find((x) => x.paras.some((p) => p.includes(feeText))) ?? doc.sections.find((x) => /ersättning/i.test(x.heading));
    if (fee) fee.paras.push(line);
    else doc.sections.push({ heading: `${doc.sections.length + 1}. Fakturering`, paras: [line] });
  }
  return { ...doc, signer: a.guardian ? { name: a.guardian, role: 'guardian' } : { name: a.actor, role: 'actor' }, unknown: [...unknown], templateVersion: tpl.version };
}

/** Is the person under 18 on this day (YYYY-MM-DD)? */
export function isMinor(birthDate: string | null, on: string): boolean {
  if (!birthDate) return false;
  const [y, m, d] = birthDate.split('-').map(Number) as [number, number, number];
  return `${y + 18}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}` > on;
}

/** The release names the production, never its internal code (owner rule 2026-10-16: codes stay out of what people read). */
const MODEL_RELEASE_SUBJECT = 'Avser KFF:s produktion {{project}} för {{client}} (”Kunden”).';
/** The first saved versions had the code in that line; an untouched copy of it reads as the line above. */
export const MODEL_RELEASE_OLD_SUBJECT = 'Avser KFF:s produktion {{project}} ({{project_code}}) för {{client}} (”Kunden”).';

/** Version 1 of the model release (draft for legal review, owner 2026-10-16). */
export const MODEL_RELEASE_SEED: TemplateDoc = {
  title: 'MODELLKONTRAKT – {{project}}',
  intro: [
    'Parter: {{kff}}, org.nr {{kff_orgno}} (”KFF”), och {{actor}}[[, född {{birth_date}}]][[, {{actor_contact}}]] (”Medverkande”).',
    MODEL_RELEASE_SUBJECT
  ],
  sections: [
    sec('assignment', 'Medverkan', ['Medverkande medverkar i produktionen[[ som {{role}}]][[ vid inspelning den {{shoot_date}}]]. Medverkande följer KFF:s anvisningar på inspelningsplatsen[[, där {{producer}} är KFF:s producent]].']),
    sec('rights', 'Rättigheter', [
      'Medverkande ger KFF rätt att spela in, redigera och använda film, stillbilder och ljud där Medverkande förekommer (”Materialet”), och att använda Medverkandes namn i samband med Materialet. KFF får överlåta rätten till Kunden.',
      'Materialet får användas i alla medier och kanaler, till exempel webb, sociala medier, annonser, tv, mässor och interna kanaler, utan begränsning i tid eller geografiskt område[[, med denna begränsning: {{usage}}]].',
      'Materialet får redigeras, kortas och kombineras med annat material, men inte användas i ett sammanhang som är kränkande för Medverkande.'
    ]),
    sec('fee', 'Ersättning', ['Medverkande får {{fee}} för medverkan och för rättigheterna i punkt {{ref:rights}}. Beloppet betalas inom 30 dagar efter inspelningen mot faktura, eller som lön med skatteavdrag om Medverkande inte har F-skatt.'], 'fee'),
    { ...sec('nofee', 'Ersättning', ['Medverkan sker utan ersättning, om inte annat har avtalats skriftligen.']), unless: 'fee' },
    sec('guardian', 'Medverkande under 18 år', ['Medverkande är under 18 år. {{guardian}}, förälder eller vårdnadshavare, godkänner avtalet för Medverkandes räkning och intygar att hen har rätt att göra det.'], 'guardian'),
    sec('data', 'Personuppgifter', [
      'KFF behandlar Medverkandes namn, kontaktuppgifter, bild och röst för att genomföra produktionen och administrera avtalet (GDPR art. 6.1 b). Uppgifterna sparas så länge Materialet används, och avtalet sparas så länge bokföringslagen kräver. Medverkande kan begära att få veta vilka uppgifter KFF har genom att kontakta KFF.'
    ]),
    sec('other', 'Övrigt', ['Medverkande kan inte kräva att Materialet tas bort efter att det har publicerats, utom när lag kräver det. Svensk lag gäller för avtalet.']),
    sec('signatures', 'Underskrift', ['Avtalet signeras elektroniskt. Signeringen sparas med namn, tidpunkt, IP-adress och en kontrollsumma av avtalstexten. Medverkande kan skriva ut eller spara avtalet från signeringssidan.'])
  ]
};

/** Version 1: KFF's templates (docs/avtalsmallar-2026-10-02.md, owner decisions 2026-10-02 … 2026-10-06). */
export const CONTRACT_TEMPLATES_SEED: ContractTemplatesData = {
  subscription: {
    title: 'KUNDAVTAL – RAMAVTAL MED ABONNEMANG',
    intro: [PARTIES, 'Avtal {{ref}}. Bygger på offert {{ref}}.'],
    sections: [
      sec('form', 'Avtalets form', ['Avtalet är ett ramavtal. Kunden har ett minimiåtagande per kalendermånad och avropar varje månad det eller de paket Kunden behöver för månaden, enligt punkt {{ref:calloff}}.']),
      sec('minimum', 'Minimiåtagande', ['Kunden åtar sig att avropa minst ett paket {{package}} per kalendermånad, motsvarande {{price}} per månad exklusive moms (minimiåtagandet).']),
      sec('calloff', 'Avrop och priser', [
        'Kunden avropar paket för en månad via KFF:s producent eller via Kundens sida hos KFF. Ett avrop kan omfatta ett eller flera paket. Följande paket kan avropas, till dessa priser per paket och månad exklusive moms:',
        '{{calloff_rows}}'
      ]),
      sec('scope', 'Omfattning per paket', [`I varje avropat paket ingår innehållet i punkt {{ref:calloff}} och dessutom {{included}}. ${EXTRA}`]),
      sec('unused', 'Månadens pris och omfattning som inte används', [
        'Varje månad faktureras som summan av de paket som avropats för månaden, dock lägst minimiåtagandet. Om inget paket avropas för en månad faktureras minimiåtagandet. Omfattning som inte används under en månad förs inte över till nästa månad och ger ingen rätt till avdrag eller återbetalning, till exempel en inspelning som inte blir av eller filmer som inte beställs. Om inspelningen ställs in eller flyttas av KFF, eller på grund av force majeure, flyttas omfattningen till en ny tid som parterna kommer överens om.'
      ]),
      OPTIONS,
      sec('startup', 'Uppstart', ['För uppstart första månaden (uppstartsmöte, planering, samarbetsytor och genomgång av grafisk profil) debiteras en engångsavgift om {{startup_fee}} exklusive moms.'], 'startup_fee'),
      sec('price', 'Pris', ['Minimiåtagandet är {{price}} per månad ({{package_key}}). Varje avropat paket kostar priset enligt punkt {{ref:calloff}}. Priserna justeras enligt Allmänna villkor § 11.', '{{ads}}']),
      sec('billing', 'Fakturering', [
        'Månadsavgiften faktureras i förskott den första vardagen varje månad, med {{payment_days}} dagars betalningsvillkor. Avrop utöver minimiåtagandet faktureras för den månad de avser. Tillval och avvikelser faktureras i efterskott på nästa månadsfaktura.'
      ]),
      sec('term', 'Avtalstid', ['Avtalet gäller från {{start}}. {{period}}']),
      sec('notice', 'Uppsägning', [
        'Avtalet kan sägas upp skriftligen med {{notice_months}} månaders uppsägningstid räknat från början av nästa kalendermånad[[, dock tidigast till utgången av den minsta avtalstiden om {{min_term}}]]. Under uppsägningstiden gäller avtalet som vanligt, med minimiåtagande, avrop och fakturering.'
      ]),
      SPECIAL,
      CONTACTS,
      APPENDICES,
      SIGNATURES
    ]
  },
  project: {
    title: 'KUNDAVTAL – RAMAVTAL PER PRODUKTION',
    intro: [PARTIES, 'Avtal {{ref}}. Bygger på offert {{ref}}.'],
    sections: [
      sec('form', 'Uppdrag och avtalets form', [
        'Avtalet är ett ramavtal[[ för ”{{project}}”]]. Kunden har ett minimiåtagande om ett antal produktioner och avropar varje produktion som det paket produktionen behöver, enligt punkt {{ref:calloff}}.'
      ]),
      sec('minimum', 'Minimiåtagande', ['Kunden åtar sig att avropa minst {{min_productions}} på nivån {{package}} inom {{period_months}} från avtalets start, till ett minimivärde av {{min_value}} exklusive moms.']),
      sec('calloff', 'Avrop och priser', [
        'Kunden avropar en produktion via KFF:s producent eller via Kundens sida hos KFF. Volympriset ({{tier}}) bestäms av det avtalade minimiantalet produktioner och gäller alla avrop inom avtalet. Följande paket kan avropas, till dessa priser per produktion exklusive moms:',
        '{{calloff_rows}}'
      ]),
      sec('scope', 'Omfattning', [`{{production_def}} I varje produktion ingår dessutom {{included}}. ${EXTRA}`]),
      sec('shortfall', 'Minimiåtagande vid periodens slut', [
        'Varje avropad produktion räknas som en produktion mot minimiåtagandet, oavsett paket. Om färre produktioner än minimiåtagandet har avropats när perioden löper ut fakturerar KFF mellanskillnaden: antalet saknade produktioner × priset för {{package_key}} på avtalets volymnivå.'
      ]),
      OPTIONS,
      sec('price', 'Pris', ['Priserna per produktion står i punkt {{ref:calloff}} och gäller hela avtalsperioden. Priserna justeras enligt Allmänna villkor § 11.', '{{ads}}']),
      sec('startup', 'Uppstart', ['För uppstart (uppstartsmöte, planering, samarbetsytor och genomgång av grafisk profil) debiteras en engångsavgift om {{startup_fee}} exklusive moms.'], 'startup_fee'),
      sec('billing', 'Fakturering', [
        'Varje avropad produktion faktureras för sig när den levereras, med {{payment_days}} dagars betalningsvillkor. Mellanskillnad enligt punkt {{ref:shortfall}} faktureras när perioden löper ut.[[ Tillval enligt punkt {{ref:options}} faktureras i efterskott när de används.]]'
      ]),
      sec('timeline', 'Tidplan', ['Avtalet gäller från {{start}}. Arbetet med en produktion startar när den avropas. {{timeline}}']),
      sec('cancel', 'Avbokning', [
        'Om Kunden avbokar en avropad produktion debiteras {{cancel_early}} av produktionens återstående pris om avbokningen sker {{cancel_days}} dagar eller mer före {{cancel_ref}}, och {{cancel_late}} om den sker senare. Kunden betalar också arbete som redan har utförts och kostnader som inte går att avboka.[[ {{reschedule_note}}]]'
      ]),
      SPECIAL,
      CONTACTS,
      APPENDICES,
      SIGNATURES
    ]
  },
  terms: {
    title: 'Klara Färdiga Film – Allmänna villkor {{terms_version}}',
    version: '2026:2',
    effectiveFrom: '2026-10-02',
    intro: ['Gäller från {{terms_date}} för uppdrag som {{kff}}, org.nr {{kff_orgno}} (”KFF”), utför åt en kund (”Kunden”).'],
    sections: [
      tsec(
        '1. Tillämpning och definitioner',
        '1.1 Villkoren gäller när ett kundavtal, projektavtal eller tillägg (”Avtalet”) hänvisar till dem. Villkoren gäller bara mellan näringsidkare.',
        '1.2 ”Material” betyder det som KFF levererar, till exempel filmer, bilder, grafik, ljud och undertexter. ”Råmaterial” betyder inspelat material som inte har redigerats och projektfiler. ”Kundmaterial” betyder det som Kunden lämnar till KFF, till exempel logotyper, grafisk profil, texter, manus, produkter, musik och personuppgifter.'
      ),
      tsec(
        '2. Beställning och omfattning',
        '2.1 Omfattning, pris och tidplan framgår av Avtalet. Arbete som inte står där ingår inte.',
        '2.2 Ändringar och tillägg ska vara skriftliga. E-post räcker för mindre tillägg som KFF bekräftar med pris. Större ändringar görs genom ett tillägg till Avtalet.',
        '2.3 Giltighetstiden för ett lösningsförslag eller en offert står i förslaget, annars 30 dagar.'
      ),
      tsec(
        '3. Kundens medverkan, material och godkännanden',
        '3.1 Kunden ska i tid lämna Kundmaterial, information, tillgång till lokaler och de personer som ska medverka, samt utse en kontaktperson som får fatta beslut.',
        '3.2 Kunden ansvarar för att Kunden har rätt att använda Kundmaterialet och att det inte gör intrång i någon annans rätt eller bryter mot lag. Det gäller också uppgifter och påståenden om Kundens produkter, till exempel hälsopåståenden.',
        '3.3 Kunden ansvarar för att Kundens egna anställda och andra personer som Kunden tar med till inspelningen har informerats och har gått med på att medverka. Det gäller också att materialet får användas på det sätt som Kunden har bestämt. Personer som KFF anlitar (talang) har avtal med KFF enligt § 7.4.',
        '3.4 Kunden ska ge samlad feedback inom tre (3) arbetsdagar efter att en version har levererats. Om Kunden är sen flyttas KFF:s leveranstider fram lika mycket. KFF får också planera om arbetet. Leveranstider i Avtalet gäller bara om Kunden lämnar material och feedback i tid.'
      ),
      tsec(
        '4. Feedbackrundor och tilläggsarbete',
        '4.1 I varje leverans ingår det antal feedbackrundor som står i Avtalet. En feedbackrunda är en samlad lista med ändringar inom den manus- och briefram som har godkänts.',
        '4.2 Ändringar som går utöver den godkända briefen eller manuset räknas som tilläggsarbete, även om en feedbackrunda finns kvar. Det gäller till exempel nytt budskap, ny inspelning, ny målgrupp eller nya format utöver de som avtalats. Detsamma gäller extra feedbackrundor.',
        '4.3 Tilläggsarbete debiteras med det timpris som står i Avtalet, annars med KFF:s gällande timpris. KFF ska informera Kunden innan tilläggsarbete som väntas bli större än två (2) timmar påbörjas.',
        '4.4 Expressleverans och liknande snabbare leveranser kan beställas mot tillägg enligt KFF:s gällande prislista.'
      ),
      tsec(
        '5. Leverans och godkännande',
        '5.1 Leverans sker digitalt via KFF:s leveranslänk eller kundportal i de format som står i Avtalet.',
        '5.2 Materialet räknas som godkänt när Kunden godkänner det. Det räknas också som godkänt om Kunden inte har lämnat skriftliga och konkreta synpunkter på fel inom fem (5) arbetsdagar efter slutleveransen, eller om Kunden publicerar materialet.',
        '5.3 Om materialet har ett fel som KFF ansvarar för, det vill säga en avvikelse från det avtalade, ska KFF rätta felet inom skälig tid utan extra kostnad. Om felet inte går att rätta har Kunden rätt till ett skäligt prisavdrag för den leverans som är felaktig. Det är Kundens enda påföljd vid fel, utöver § 14.',
        '5.4 KFF sparar slutleveranser i minst 24 månader efter leveransen. Råmaterial och projektfiler lämnas inte ut, om det inte avtalas skriftligen och mot ersättning.'
      ),
      tsec(
        '6. Resor, tider och övertid',
        '6.1 Resor och logi utanför Stockholm debiteras enligt självkostnad. Restid debiteras med halva timpriset.',
        '6.2 Arbete som Kunden begär utanför vardagar kl. 08–17 debiteras med dubbelt timpris, om inget annat har avtalats.'
      ),
      tsec(
        '7. Rättigheter',
        '7.1 KFF, eller KFF:s licensgivare, har upphovsrätten och andra immateriella rättigheter till Materialet och Råmaterialet.',
        '7.2 När Kunden har betalat för materialet får Kunden en icke-exklusiv, tidsobegränsad nyttjanderätt att använda Materialet i Kundens egen marknadsföring och kommunikation i alla digitala kanaler, till exempel webb, sociala medier, digital annonsering och intern kommunikation. TV, bio och tryck/utomhus kräver skriftligt avtal. Kunden får göra enklare ändringar som att korta och översätta, men inte överlåta eller upplåta rätten till någon annan utan KFF:s skriftliga samtycke. Undantag är koncernbolag och byråer som publicerar för Kundens räkning.',
        '7.3 Musik, bildbanksmaterial, typsnitt och liknande från tredje part licensieras för den användning som står i Avtalet och följer licensgivarens villkor. KFF ansvarar för att musik och bildbanksmaterial som KFF väljer är licensierat för publicering i Kundens digitala kanaler för material som publiceras under avtalstiden. Material som har publicerats får ligga kvar efter att avtalet har upphört. Kunden får inte återanvända musiken separat. Om Kunden lämnar egen musik ansvarar Kunden för licensen.',
        '7.4 Om skådespelare, speakers eller andra medverkande som KFF anlitar finns med i materialet får det bara användas i de medier, det område och den tid som står i Avtalet eller som KFF meddelar skriftligen. Utan särskild uppgift gäller digitala kanaler i Sverige i tolv (12) månader från första publicering. Längre eller annan användning kräver förlängning mot ersättning. Kunden ansvarar för att ta ned materialet när nyttjandetiden har gått ut. KFF påminner i god tid.',
        '7.5 KFF får visa Materialet i sin egen marknadsföring, i portfolio, på webb och i sociala medier, och i tävlingar, när KFF vill, om inte Kunden har angett annat. Kunden kan när som helst skriftligen ange att visst material inte får visas, till exempel interna eller konfidentiella filmer; sådant material visas inte.',
        '7.6 Kundmaterial tillhör Kunden. KFF får använda det bara för att utföra Avtalet.'
      ),
      tsec(
        '8. AI-verktyg',
        '8.1 KFF får använda AI-verktyg i produktionen, till exempel för idéer, manusutkast, storyboard, transkribering, undertexter, översättning, ljudrensning och redigering. KFF väljer leverantörer som behandlar data inom EU/EES eller med skyddsåtgärder som är tillräckliga, och som inte tränar sina modeller på Kundens material.',
        '8.2 Bilder, film, röster eller personer som är AI-genererade och används i slutleveransen ska anges för Kunden innan leverans. Det kräver Kundens godkännande. KFF skapar inga digitala kopior av en verklig persons röst eller utseende utan att personen skriftligen har samtyckt.',
        '8.3 AI-genererade delar kan sakna upphovsrättsligt skydd. KFF garanterar inte att sådana delar är exklusiva för Kunden. Kunden ansvarar för att märka materialet enligt lag och plattformarnas regler när Kunden publicerar det.'
      ),
      tsec(
        '9. Sekretess',
        '9.1 Var och en av parterna ska hålla den andra partens konfidentiella information hemlig och bara använda den för att fullgöra Avtalet. Exempel är affärsplaner, opublicerade produkter, priser, villkor och inte publicerat material. Skyldigheten gäller under avtalstiden och tre (3) år därefter.',
        '9.2 Skyldigheten gäller inte information som är allmänt känd utan att parten har brutit mot avtalet, som parten redan hade lagligen, eller som parten måste lämna ut enligt lag, myndighetsbeslut eller börsregler.',
        '9.3 KFF ser till att anställda och underleverantörer har samma sekretesskyldighet.'
      ),
      tsec(
        '10. Personuppgifter',
        '10.1 Varje part ansvarar för sin egen behandling av personuppgifter enligt dataskyddsförordningen (GDPR).',
        '10.2 När KFF behandlar personuppgifter för Kundens räkning gäller det personuppgiftsbiträdesavtal (PUB) som parterna har undertecknat. Det kan till exempel gälla bild och röst av Kundens anställda i material som Kunden bestämmer över, eller kontaktlistor. KFF:s behandling av kontaktuppgifter för att administrera avtalet och av talang som KFF anlitar sker med KFF som personuppgiftsansvarig, enligt KFF:s integritetspolicy.'
      ),
      tsec(
        '11. Pris och prisjustering',
        '11.1 Alla priser anges i SEK exklusive moms. Om faktureringen sker i annan valuta gäller växelkursen på fakturadagen.',
        '11.2 Löpande priser justeras den 1 januari varje år efter hur SCB:s konsumentprisindex (KPI) har förändrats under föregående kalenderår. Justeringen är dock minst 2 % och högst 6 %.',
        '11.3 Om externa kostnader för en enskild produktion ökar efter avtalet och KFF inte rimligen kunde förutse det, till exempel lokaler, talang eller licenser, får KFF ändra priset för den produktionen. KFF ska då informera Kunden innan kostnaden uppstår.'
      ),
      tsec(
        '12. Betalning och dröjsmål',
        '12.1 Betalning ska ske inom 30 dagar netto från fakturadatum, om inget annat står i Avtalet. Löpande avgifter faktureras i förskott. Det gäller oavsett om månadens produktion är planerad eller levererad, med undantag för det som står i kundavtalet om inställd inspelning.',
        '12.2 Om betalningen är sen har KFF rätt till dröjsmålsränta med referensräntan + 12 procentenheter. KFF har också rätt till påminnelseavgift om 60 kronor och förseningsersättning om 450 kronor (lag 1981:739 och 2013:1240).',
        '12.3 Om betalningen är mer än femton (15) dagar sen efter en påminnelse får KFF stoppa pågående arbete och leveranser tills betalningen har kommit in. Nyttjanderätten enligt § 7.2 gäller först när full betalning för materialet har skett.'
      ),
      tsec(
        '13. Avbokning och ombokning av inspelning',
        '13.1 Om Kunden flyttar en inspelning mindre än sju (7) dagar före inspelningsdagen, av skäl som Kunden rimligen kunde förutse, debiteras en ombokningsavgift om 5 000 kronor. Kunden betalar också externa kostnader som redan har uppstått eller inte går att avboka, till exempel talang, studio, utrustning och resor.',
        '13.2 Om Kunden avbokar ett projekt helt debiteras: arbete som redan har utförts, kostnader som inte går att avboka, och 20 % av det återstående projektpriset om avbokningen sker 7 dagar eller mer före inspelningen, eller 50 % om den sker senare. I ett abonnemang gäller kundavtalets regler om omfattning som inte används.',
        '13.3 Om KFF måste flytta en inspelning, till exempel på grund av sjukdom, flyttas den utan kostnad för Kunden till nästa lämpliga datum.',
        '13.4 För inspelningar utomhus som beror på vädret avgör KFF och Kunden tillsammans senast dagen före om inspelningen blir av. Om den flyttas på grund av väder tas ingen avgift ut, men externa kostnader debiteras.'
      ),
      tsec(
        '14. Ansvarsbegränsning',
        '14.1 En part ersätter den andra partens direkta skador som orsakats av vållande. Ingen part ansvarar för indirekta skador, till exempel utebliven vinst, förlorad omsättning, förlorad data, skador på grund av annonsresultat eller anspråk från tredje man, utöver vad som står i § 14.3.',
        '14.2 KFF:s samlade ansvar enligt Avtalet är begränsat till ett belopp som motsvarar vad Kunden har betalat enligt Avtalet under de tolv (12) senaste månaderna före skadan, för projekt: projektpriset.',
        '14.3 Kunden håller KFF skadeslöst om någon riktar anspråk mot KFF på grund av Kundmaterial, Kundens påståenden i materialet eller Kundens användning utöver Avtalet.',
        '14.4 Begränsningarna gäller inte vid uppsåt, grov vårdslöshet eller brott mot sekretess eller dataskydd. Det gäller dock inte för indirekta skador.',
        '14.5 Ett krav ska framföras skriftligen inom sex (6) månader från det att skadan upptäcktes eller borde ha upptäckts.'
      ),
      tsec(
        '15. Force majeure',
        '15.1 En part behöver inte fullgöra en skyldighet och betalar inte skadestånd om den hindras av omständigheter utanför partens kontroll som parten inte rimligen kunde förutse. Exempel är krig, omfattande arbetskonflikt, blockad, brand, naturkatastrof, allvarlig smittspridning, myndighetsbeslut eller omfattande avbrott i el, tele eller molntjänster. Parten ska meddela den andra parten utan dröjsmål.',
        '15.2 Om hindret varar längre än tolv (12) veckor får var och en av parterna häva Avtalet utan skadeståndsskyldighet. Det som redan har utförts betalas.'
      ),
      tsec(
        '16. Avtalsbrott och uppsägning i förtid',
        '16.1 En part får säga upp Avtalet att upphöra omedelbart om den andra parten bryter mot Avtalet i väsentlig del och inte har rättat det inom trettio (30) dagar efter en skriftlig anmärkning. Om betalningen är sen gäller femton (15) dagar efter påminnelse.',
        '16.2 Det gäller också om den andra parten går i konkurs, inleder företagsrekonstruktion, ställer in betalningarna eller på annat sätt kan antas vara på obestånd.',
        '16.3 När Avtalet upphör gäller fortfarande de bestämmelser som är tänkta att gälla efteråt, till exempel nyttjanderätten till betalt material, sekretess, ansvar och tvistlösning. Kunden ska betala för det som har levererats och för kostnader som inte går att avboka.'
      ),
      tsec(
        '17. Underleverantörer och överlåtelse',
        '17.1 KFF får anlita underleverantörer, till exempel fotografer, klippare, talang och studior. KFF ansvarar för dem som för sig själv.',
        '17.2 En part får inte överlåta Avtalet utan den andra partens skriftliga samtycke. KFF får dock överlåta rätten till betalning, till exempel vid factoring, och överlåta Avtalet till ett bolag i samma koncern eller vid överlåtelse av verksamheten.'
      ),
      tsec('18. Meddelanden', 'Meddelanden enligt Avtalet ska vara skriftliga, och e-post räcker. Uppsägning ska skickas till den andra partens kontaktperson eller den adress som står i Avtalet.'),
      tsec(
        '19. Hela avtalet och ordning mellan handlingar',
        '19.1 Avtalet med bilagor är hela överenskommelsen och ersätter tidigare åtaganden och offerter om samma sak.',
        '19.2 Om handlingarna säger olika saker gäller de i denna ordning: (1) skriftliga tillägg, (2) kundavtalet eller projektavtalet, (3) personuppgiftsbiträdesavtalet när det gäller personuppgifter, (4) dessa Allmänna villkor, (5) lösningsförslag eller offert.',
        '19.3 Om en bestämmelse är ogiltig påverkar det inte resten av Avtalet. Bestämmelsen ska då ändras skäligt.',
        '19.4 KFF får ändra dessa villkor. Ändringar gäller för avtal som redan finns tidigast trettio (30) dagar efter att Kunden har informerats. Om en ändring är väsentligt till nackdel för Kunden får Kunden säga upp Avtalet till den dag ändringen börjar gälla.'
      ),
      tsec(
        '20. Tillämplig lag och tvister',
        '20.1 Svensk lag gäller för Avtalet.',
        '20.2 Tvister ska i första hand lösas genom förhandling. Om parterna inte kommer överens avgörs tvisten av allmän domstol med Stockholms tingsrätt som första instans.'
      )
    ]
  },
  dpa: {
    title: 'Personuppgiftsbiträdesavtal',
    intro: [
      'Parter: {{client}}, org.nr {{orgno}} (”Personuppgiftsansvarig”), och {{kff}}, org.nr {{kff_orgno}} (”KFF” eller ”Biträdet”). Bilagan hör till avtal {{ref}} (”Huvudavtalet”) och undertecknas tillsammans med det.'
    ],
    sections: [
      tsec(
        '1. Syfte och instruktion',
        '1.1 KFF behandlar personuppgifter för den Personuppgiftsansvarigas räkning bara för att fullgöra Huvudavtalet och enligt den Personuppgiftsansvarigas dokumenterade instruktioner. Instruktionerna finns i Huvudavtalet och i denna bilaga.',
        '1.2 KFF ska genast informera den Personuppgiftsansvariga om KFF anser att en instruktion strider mot dataskyddsreglerna.'
      ),
      tsec(
        '2. Behandlingen',
        '– Ändamål: produktion, redigering, granskning, leverans och arkivering av film- och bildmaterial enligt Huvudavtalet.',
        '– Kategorier av registrerade: den Personuppgiftsansvarigas anställda, kunder och andra personer som medverkar eller syns i materialet, och kontaktpersoner.',
        '– Kategorier av personuppgifter: namn, titel, kontaktuppgifter, bild, film och röst. Inga känsliga personuppgifter, om det inte avtalas särskilt.',
        '– Behandlingens art: insamling vid inspelning, lagring, redigering, överföring till den Personuppgiftsansvariga och radering.',
        '– Varaktighet: under Huvudavtalets löptid och därefter enligt punkt 8.'
      ),
      tsec(
        '3. Säkerhet',
        'KFF vidtar lämpliga tekniska och organisatoriska åtgärder enligt art. 32. Det betyder åtkomst bara för behöriga, tvåfaktorsinloggning i molntjänster, kryptering vid överföring, behörighetsstyrda leveranslänkar och loggning. KFF:s personal och underleverantörer har tystnadsplikt.'
      ),
      tsec(
        '4. Underbiträden',
        '4.1 Den Personuppgiftsansvariga godkänner följande underbiträden: Google Workspace (lagring och samarbete), SignWell (e-signering), Ziflow (granskning) och Google Vertex AI (AI-tjänster, region EU).',
        '4.2 KFF informerar den Personuppgiftsansvariga minst trettio (30) dagar innan KFF byter eller lägger till ett underbiträde. Den Personuppgiftsansvariga får invända av sakliga skäl. Om parterna inte kommer överens får den Personuppgiftsansvariga säga upp den berörda delen av Huvudavtalet.',
        '4.3 KFF avtalar med varje underbiträde om samma skyldigheter som i denna bilaga och ansvarar för underbiträdet.'
      ),
      tsec(
        '5. Överföring till tredje land',
        'Personuppgifter behandlas inom EU/EES. Överföring utanför EU/EES sker bara med giltig överföringsmekanism enligt kapitel V i GDPR, till exempel EU-kommissionens standardavtalsklausuler eller EU–US Data Privacy Framework.'
      ),
      tsec(
        '6. Bistånd',
        'KFF hjälper den Personuppgiftsansvariga, i rimlig omfattning och mot ersättning för större insatser, att besvara begäranden från registrerade. Exempel är begäran om tillgång, radering eller invändning, och att ett klipp ska tas bort. KFF hjälper också till med konsekvensbedömningar och förhandssamråd och vid personuppgiftsincidenter.'
      ),
      tsec(
        '7. Personuppgiftsincidenter',
        'KFF meddelar den Personuppgiftsansvariga utan onödigt dröjsmål, och senast inom fyrtioåtta (48) timmar efter att KFF har fått kännedom om en incident. Meddelandet ska innehålla den information som behövs enligt art. 33.3.'
      ),
      tsec(
        '8. Radering och återlämnande',
        'När Huvudavtalet upphör, eller tidigare om den Personuppgiftsansvariga begär det, raderar KFF personuppgifterna eller lämnar tillbaka dem. Undantag är slutleveranser som KFF får spara enligt Allmänna villkor § 5.4 och § 7.5, och uppgifter som KFF måste spara enligt lag. Råmaterial raderas senast 12 månader efter sista leverans.'
      ),
      tsec(
        '9. Granskning',
        'KFF ger den Personuppgiftsansvariga den information som behövs för att visa att art. 28 följs. KFF tillåter också granskningar efter skälig förvarning, högst en gång per år om det inte finns särskilda skäl. Den Personuppgiftsansvariga betalar sina egna kostnader.'
      ),
      tsec('10. Ansvar och giltighet', 'Ansvaret följer art. 82 i GDPR och i övrigt Allmänna villkor § 14. Bilagan gäller så länge KFF behandlar personuppgifter för den Personuppgiftsansvarigas räkning.')
    ]
  },
  model: MODEL_RELEASE_SEED
};

export const CONTRACT_TEMPLATES_SEED_NOTE = "KFF's templates from docs/avtalsmallar-2026-10-02.md (Allmänna villkor 2026:2) – draft for legal review";
